import { logNewRelicPageAction } from '@makemydeal/dr-activities-common';
import type { PurchaseHistoryCompletionServiceData } from '@makemydeal/dr-dash-types';

import { SOCKET_EVENT_IGNORED } from '../../actionTypes/socketActionTypes';
import { purchaseHistoryEventReceived } from '../purchaseHistoryActionCreators';
import * as offerReduxSelectors from '../../selectors/offerRedux';
import * as purchaseHistorySelectors from '../../selectors/purchaseHistorySelectors';
import { createLoggingContextSelector } from '../../selectors/loggingContextSelector';
import type { TopicHandler } from './types';

/**
 * The publishing service's own constant (`dr-services-deal-pulse` `publishBroadcast.ts`
 * `BROADCAST_TOPIC`). The two socket managers repeat the literal inline, so a change is three places.
 */
export const PURCHASE_HISTORY_TOPIC = 'dealPulsePurchaseHistory';

/**
 * NOT `createSocketLoggingContext`: that spreads `eventData: event.data`, which on this topic is the
 * record array — every payment, gross and rate for a real customer. Fields are named individually.
 */
const purchaseHistoryLoggingContext = (state: any, event: any) => ({
    ...createLoggingContextSelector(state),
    eventTopic: event.topic,
    connectionId: event.connectionId
});

export const handlePurchaseHistory: TopicHandler = (state, event) => {
    // 1. Subscribe ack. A hard stop, not `null`: the generic path would mistake it for an empty result.
    if (event.message?.includes?.('subscribe')) {
        return { type: SOCKET_EVENT_IGNORED, payload: event };
    }

    const completion = event.data as PurchaseHistoryCompletionServiceData | undefined;

    // 2. Fan-out defence: the service cannot verify the deal id it broadcasts on, so confirm the push
    //    belongs to the deal this browser has open.
    const openDealXgId = offerReduxSelectors.getDealXgId(state);
    if (!openDealXgId || completion?.dealId !== openDealXgId) {
        logNewRelicPageAction('MV:PurchaseHistory:WebSocketReceived:FanOutFiltered', {
            ...purchaseHistoryLoggingContext(state, event),
            reason: openDealXgId ? 'dealxgid_mismatch' : 'no_open_dealxgid'
        });
        return { type: SOCKET_EVENT_IGNORED, payload: event };
    }

    // 3. Accept ONLY while a request is in flight — one flag covers a late arrival, a duplicate push
    //    and an unsolicited one. The three sub-cases are distinguished for New Relic.
    if (!purchaseHistorySelectors.getIsPurchaseHistoryRequestOutstanding(state)) {
        const slice = purchaseHistorySelectors.getPurchaseHistory(state);
        const reason =
            slice.requestedAtMs === null
                ? 'never_requested'
                : slice.status === 'ready' || slice.status === 'empty'
                ? 'already_resolved'
                : 'panel_closed_resolved';

        logNewRelicPageAction('MV:PurchaseHistory:WebSocketReceived:NotOutstanding', {
            ...purchaseHistoryLoggingContext(state, event),
            reason
        });
        return { type: SOCKET_EVENT_IGNORED, payload: event };
    }

    const requestedAtMs = purchaseHistorySelectors.getPurchaseHistoryRequestedAtMs(state);

    logNewRelicPageAction('MV:PurchaseHistory:WebSocketReceived', {
        ...purchaseHistoryLoggingContext(state, event),
        // Bounded, countable facts only — never a record value.
        outcome: completion.state,
        recordCount: completion.count,
        failureReason: completion.failureReason ?? null,
        requestId: completion.requestId ?? null,
        receivedTimestamp: new Date().toISOString(),
        // The push carries no timestamp, so this measures the manager's actual wait. `null`, not `0`,
        // when there is no anchor.
        requestToRenderDelayMs: requestedAtMs === null ? null : Date.now() - requestedAtMs
    });

    return purchaseHistoryEventReceived(completion);
};
